import * as React from "react";
import { useNotify } from "react-admin";
import {
  Box, Card, CardContent, Typography, TextField, Button, Stack,
  Alert, Chip,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { httpClient } from "../../api/httpClient";
import { API_BASE_URL } from "../../api/config";

const StripeConnect = () => {
  const [proId, setProId] = React.useState("");
  const [status, setStatus] = React.useState(null);
  const [transfers, setTransfers] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const notify = useNotify();

  const loadAll = async () => {
    if (!proId.trim()) return;
    setLoading(true);
    try {
      const [{ json: s }, { json: t }] = await Promise.all([
        httpClient(`${API_BASE_URL}/pro/${proId}/stripe-connect/status`).catch(() => ({ json: null })),
        httpClient(`${API_BASE_URL}/pro/${proId}/transfers`).catch(() => ({ json: [] })),
      ]);
      setStatus(s);
      setTransfers(Array.isArray(t) ? t : t?.data || []);
    } catch (e) {
      notify(`Erreur : ${e.message}`, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const startOnboarding = async () => {
    try {
      // refreshUrl & returnUrl sont des @RequestParam obligatoires : on
      // renvoie vers la page courante du BO après l'onboarding Stripe.
      const backUrl = window.location.href;
      const qs = new URLSearchParams({ refreshUrl: backUrl, returnUrl: backUrl });
      const { json } = await httpClient(`${API_BASE_URL}/pro/${proId}/stripe-connect/start?${qs}`, {
        method: "POST",
      });
      notify("Onboarding initié", { type: "success" });
      if (json?.url) window.open(json.url, "_blank");
    } catch (e) {
      notify(`Erreur : ${e.message}`, { type: "error" });
    }
  };

  const refreshLink = async () => {
    try {
      // Body { returnUrl } requis (400 sinon)
      const { json } = await httpClient(`${API_BASE_URL}/pro/${proId}/stripe-connect/update-link`, {
        method: "POST",
        body: JSON.stringify({ returnUrl: window.location.href }),
      });
      notify("Lien rafraîchi", { type: "success" });
      if (json?.url) window.open(json.url, "_blank");
    } catch (e) {
      notify(`Erreur : ${e.message}`, { type: "error" });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <StorefrontIcon /> Stripe Connect
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Onboarding Stripe Connect du pro et liste des virements.
      </Alert>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField size="small" label="ID Pro" value={proId}
                onChange={(e) => setProId(e.target.value)} />
              <Button variant="contained" onClick={loadAll}
                disabled={loading || !proId.trim()}>
                Charger
              </Button>
              <Button variant="outlined" onClick={startOnboarding}
                disabled={!proId.trim()}>
                Démarrer onboarding
              </Button>
              <Button variant="outlined" onClick={refreshLink}
                disabled={!proId.trim()}>
                Rafraîchir le lien
              </Button>
            </Box>
            {status && (
              <Box>
                <Typography variant="h6">Statut</Typography>
                <Chip label={status.status || "—"} color={
                  status.status === "active" ? "success" :
                  status.status === "pending" ? "warning" : "default"
                } />
                <Box component="pre" sx={{
                  bgcolor: "#f5f7fa", p: 1.5, borderRadius: 1,
                  fontFamily: "monospace", fontSize: 12, mt: 1, maxHeight: 200, overflow: "auto",
                }}>
                  {JSON.stringify(status, null, 2)}
                </Box>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
      {transfers.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Virements ({transfers.length})</Typography>
            <Box component="pre" sx={{ fontSize: 12, maxHeight: 400, overflow: "auto" }}>
              {JSON.stringify(transfers, null, 2)}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default { list: StripeConnect };
